/** Organization User Permission Management — Models & Types */

import { USER_ROLES } from '../constants/app.constants';

/** Possible user roles in the system. */
export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/** Role object as returned by the API. */
export interface RoleInfo {
  value: UserRole;
  label: string;
}

/** Roles that can be assigned via the invite/edit forms (Super Admin excluded). */
export const ASSIGNABLE_ROLES: RoleInfo[] = [
  { value: USER_ROLES.ADMIN, label: 'Admin' },
  { value: USER_ROLES.USER, label: 'User' },
];

/** Valid assignable role values for form submissions. */
export type AssignableRole = typeof USER_ROLES.ADMIN | typeof USER_ROLES.USER;

/** Human-readable display names for each role. */
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.ADMIN]: 'Admin',
  [USER_ROLES.USER]: 'User',
} as Record<UserRole, string>;

/** An organization user as returned by the API. */
export interface OrganizationUser {
  id: number;
  name: string;
  email: string;
  role: RoleInfo;
  invited_by: number | null;
}

/** Request body for POST /organization-permission/users. */
export interface InviteUserRequest {
  name: string;
  email: string;
  password: string;
  role: typeof USER_ROLES.ADMIN | typeof USER_ROLES.USER;
}

/** Request body for PUT /organization-permission/users/:id. */
export interface UpdateUserRequest {
  name: string;
  role: typeof USER_ROLES.ADMIN | typeof USER_ROLES.USER;
}

/** Request body for PUT /organization-permission/users/:id/password. */
export interface ResetPasswordRequest {
  password: string;
}

/** Pagination metadata returned by the list endpoint. */
export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/** Paginated response for the users list endpoint. */
export interface UserListResponse {
  status: boolean;
  message: string;
  data: OrganizationUser[];
  meta: PaginationMeta;
}
